/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import useHttp from "hooks/http.hook";
import {
  AssetInterface,
  HeaderEnum,
  MediaCategoryEnum,
  MediaSourceEnum,
  MediaStatusEnum,
  PatientInsuranceInterface,
  PatientPharmacyInterface,
  PersonInterface,
  PharmacyInterface,
  UserInterface,
  UserRolesEnum,
} from "@malmur-technologies/medicalsystem-shared";
import { FormActionType, EntityTypeEnum } from "types/form.enum";
import { getValidUserModel, getValidPersonModel } from "../../utils/model.utils";
import { getUserRoleEntityType } from "../../utils/form.utils";
import { useMemo, useState } from "react";
import Box from "../generals/Box";
import { Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import Card from "./../generals/Card";
import UserCreationForm from "./UserForm";
import PersonForm from "./PersonForm";
import { ExtendedPersonInterface } from "interfaces";
import EntityModelForm from "./custom/EntityFormSelector";
import { CoreAPI_URLs } from "constants/http.const";
import { H1 } from "components/generals/_Titles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Dialog from "components/generals/Dialog";
import DialogTitle from "components/generals/DialogTitle";
import DialogContent from "components/generals/DialogContent";
import DialogContentText from "components/generals/DialogContentText";
import DialogActions from "components/generals/DialogActions";
import Button from "components/generals/Button";
import { passwordValidation } from "utils/form.utils";
import { useSelector } from "react-redux";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";
import { t } from "i18next";

/**
 * Represents a single step in the multi-step form wizard.
 */
export interface FormStep {
  label: string;
  id: string;
}

/**
 * Properties for the NewStepperForm component.
 */
export interface Props<T extends Object> {
  model: T;
  type?: UserRolesEnum;
  redirect: string;
  isDoctor?: boolean;
  postProcessingHandler?: (model: Partial<T>, payload: ExtendedPersonInterface[]) => void;
}

/**
 * Multi-step form wizard for creating new users with associated person and entity records.
 * Implements a 3-step registration process with progress tracking.
 * 
 * Features:
 * - Three-step wizard: User Account → Personal Information → Entity Details
 * - Progress indicator with Material UI Stepper component
 * - Step navigation with back/next buttons
 * - Form validation at each step
 * - User creation with username and password
 * - Person/demographic information capture
 * - Entity-specific form (Patient, Doctor, Nurse, etc.)
 * - Profile image upload with file preview
 * - Pharmacy relationship management for patients
 * - Insurance relationship management for patients
 * - Parent/guardian relationships for minor patients
 * - Password validation and confirmation
 * - Automatic navigation on successful submission
 * - Post-processing callback for custom logic
 * 
 * Steps:
 * 1. User Creation: Username, password, email, role selection
 * 2. Person Information: Demographics, contact info, address, relationships
 * 3. Entity Details: Role-specific information (patient/doctor/nurse details)
 * 
 * Role-Based Processing:
 * - Patient: Creates user + person + patient + pharmacy/insurance relations
 * - Doctor: Creates user + person + provider with doctor credentials
 * - Nurse: Creates user + person + provider with nurse credentials
 * - Admin/Receptionist: Creates user + person only
 * 
 * Parent/Guardian Support:
 * - For minor patients, captures parent/guardian information
 * - Creates parent person records automatically
 * - Links parent-child relationships
 * 
 * Security:
 * - Password validation with complexity requirements
 * - Confirmation modal for navigation away from form
 * - Secure file upload for profile images
 * - Role-based API endpoint selection
 * - Transaction-style creation (all-or-nothing)
 * 
 * @param {Props<T>} props - Component properties
 * @param {T} props.model - Initial model data for the entity being created
 * @param {UserRolesEnum} props.type - Optional user role type for role-specific logic
 * @param {string} props.redirect - URL to redirect after successful creation
 * @param {boolean} props.isDoctor - Optional flag to indicate doctor-specific workflow
 * @param {Function} props.postProcessingHandler - Optional callback after successful save
 * 
 * @returns {JSX.Element} Multi-step form wizard with user, person, and entity creation
 */
const NewStepperForm = <T extends Object>(props: Props<T>) => {
  const navigate = useNavigate();
  const [_isLoading, _error, sendRequest] = useHttp();
  const [image, setImage] = useState<File | null>(null);
  const [providerAssetImage, setProviderAssetImage] = useState<File | null>(null);
  const [pharmacyRelationList, setPharmacyRelationList] = useState<PatientPharmacyInterface[]>([]);
  const [defaultPharmacy, setDefaultPharmacy] = useState<Partial<PharmacyInterface>>();
  const [insuranceRelationList, setInsuranceRelationList] = useState<PatientInsuranceInterface[]>([]);

  const { t } = useTranslation();
  const generalSettings = useSelector(selectGeneralSettings);
  const shouldCreatePatientUser = generalSettings?.isPatientUserEnabled ?? true;

  const [newModel, setNewModel] = useState<Partial<T>>(props.model);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const [errorModalContent, setErrorModalContent] = useState<{ title: string, message: string }>({ title: '', message: '' });
  const [activeStep, setActiveStep] = useState(0);
  const isEditMode = useMemo(() => !!newModel["person"]?.id, [newModel]);
  const totalSteps = useMemo(() => {
    if ([UserRolesEnum.ADMIN, UserRolesEnum.ROOT, UserRolesEnum.MAINTAINER, UserRolesEnum.SECRETARY_RECEPTIONIST].includes(props.type)) {
      return 2;
    }
    if (props.type === UserRolesEnum.PATIENT && isEditMode) {
      return 2;
    }
    if (props.type === UserRolesEnum.PATIENT && !shouldCreatePatientUser) {
      return 2;
    }
    return 3;
  }, [props.type, shouldCreatePatientUser, isEditMode]);

  const isTheLastStep = useMemo<boolean>(() => activeStep === totalSteps - 1, [activeStep, totalSteps]);
  const isTheFirstStep = useMemo<boolean>(() => activeStep === 0, [activeStep]);

  const backStepHandler = () => {
    setActiveStep(activeStep - 1);
  };

  let ban: boolean;
  let parentListLocal: ExtendedPersonInterface[];

  //HTTP Request:
  const processModel = async (model: T) => {
    let urlModel = CoreAPI_URLs.PATIENTS_CREATE_ALL_MODEL;
    let requestData;

    // Check if we're reusing an existing person+user (person has ID and user with ID)
    const isReusingPersonWithUser = model["person"]["id"] && model["person"]["user"]?.id;

    if (isReusingPersonWithUser) {
      // Reusing existing person+user: send only person.id and user.id to link entities
      // Do NOT send full user object to avoid auth validation errors
      // Include targetRole to inform backend which role should be added to the existing user
      const { user, ...personWithoutUser } = model["person"];
      const personForRequest = {
        ...personWithoutUser,
        id: model["person"]["id"],
        user: {
          id: user.id
        }
      };
      requestData = {
        person: personForRequest,
        model: model,
        targetRole: props.type // Send the target role (DOCTOR, NURSE, HEAD_NURSE, PATIENT)
      };
      // Determine endpoint based on type prop
      if (props.type === UserRolesEnum.PATIENT) {
        urlModel = CoreAPI_URLs.PATIENTS_CREATE_ALL_MODEL;
        if (defaultPharmacy) model["defaultPharmacy"] = defaultPharmacy;
      } else if (props.type === UserRolesEnum.DOCTOR || props.type === UserRolesEnum.NURSE || props.type === UserRolesEnum.HEAD_NURSE) {
        urlModel = CoreAPI_URLs.PROVIDER_CREATE_ALL_MODEL;
        setPharmacyRelationList(null);
      }
    } else if (model["person"]["user"] != undefined) {
      const modelType = model["person"]["user"];
      requestData = {
        user: model["person"]["user"],
        person: model["person"],
        model: model,
      };
      if (modelType.roles[0] === UserRolesEnum.PATIENT) {
        urlModel = CoreAPI_URLs.PATIENTS_CREATE_ALL_MODEL;
        if (defaultPharmacy) model["defaultPharmacy"] = defaultPharmacy;
      } else if (modelType.roles[0] === UserRolesEnum.DOCTOR || modelType.roles[0] === UserRolesEnum.NURSE || modelType.roles[0] === UserRolesEnum.HEAD_NURSE) {
        urlModel = CoreAPI_URLs.PROVIDER_CREATE_ALL_MODEL;
        setPharmacyRelationList(null);
      } else if (modelType.roles[0] === UserRolesEnum.ROOT || modelType.roles[0] === UserRolesEnum.ADMIN || modelType.roles[0] === UserRolesEnum.MAINTAINER || modelType.roles[0] === UserRolesEnum.SECRETARY_RECEPTIONIST) {
        urlModel = CoreAPI_URLs.USERS_CREATE_ALL_MODEL;
      }
    } else {
      requestData = {
        person: model["person"],
        model: model,
      };
    }
    if (requestData) {//Validate that password does not contain username, first name, or last name
      const password = requestData.user?.auth?.password ?? "";
      const username = requestData.user?.username ?? "";
      const name = requestData.person?.name ?? "";
      const lastname = requestData.person?.lastname ?? "";
      const result = passwordValidation(t, password, username, lastname, name);
      if (typeof result === "string" && result.includes(t("formUtils.passwordValidation"))) {
        setErrorModalContent({
          title: t("stepperForm.weakpassword"),
          message: t("stepperForm.passwordValidation")
        });
        setOpenErrorModal(true);
        return;
      }
    }
    await sendRequest<T>(
      urlModel,
      onModelCreationHandler,
      "POST",
      JSON.stringify(requestData),
      null,
      {
        indexRequest: 3,
        totalRequests: 3,
        successMessage: t("stepperForm.creationSuccess"),
      }
    );
  };

  const onModelCreationHandler = async (model: T) => {
    if (image) {
      const data = {
        mimetype: image.type,
        source: MediaSourceEnum.OTHER,
        category: MediaCategoryEnum.PROFILE,
        orgName: image.name,
        status: MediaStatusEnum.PENDING,
      };
      await sendRequest<AssetInterface>(
        CoreAPI_URLs.ASSETS,
        async (responseAsset) => {
          if (responseAsset) {
            const formData = new FormData();
            formData.append("file", image);
            await sendRequest<AssetInterface>(
              CoreAPI_URLs.ASSETS_UPLOAD,
              async (responseAssetUpload) => {
                if (responseAssetUpload) {
                  const UpdatePerson: PersonInterface = {
                    ...model["person"],
                    photo: responseAssetUpload,
                  };
                  await sendRequest<PersonInterface>(
                    CoreAPI_URLs.PERSONS_GENERAL_OPERATIONS,
                    null,
                    "PUT",
                    JSON.stringify(UpdatePerson),
                    [{ header: HeaderEnum.RESOURCE_ID, value: model["person"].id }],
                    { successMessage: t("stepperForm.creationSuccess") }
                  );
                }
              },
              "POST",
              formData,
              [{ header: HeaderEnum.RESOURCE_ID, value: responseAsset.id }]
            );
          }
        },
        "POST",
        JSON.stringify(data)
      );
    }
    if (pharmacyRelationList) {
      pharmacyRelationList.forEach(async (pharmacy) => {
        pharmacy.patient = model["model"];
        await sendRequest<T>(
          CoreAPI_URLs.PATIENT_PHARMACY_RELATION_GENERAL_OPERATION,
          null,
          "POST",
          JSON.stringify(pharmacy)
        );
      });
    }
    if (insuranceRelationList) {
      insuranceRelationList.forEach(async (insurance) => {
        insurance.patient = model["model"];
        await sendRequest(
          CoreAPI_URLs.PATIENT_INSURANCE_GENERAL_OPERATION,
          null,
          "POST",
          JSON.stringify(insurance)
        );
      });
    }
    // Create provider-asset relation if a provider asset image was selected
    if (providerAssetImage && model["model"]?.id) {
      const assetData = {
        mimetype: providerAssetImage.type,
        source: MediaSourceEnum.OTHER,
        category: MediaCategoryEnum.PROFILE,
        orgName: providerAssetImage.name,
        status: MediaStatusEnum.PENDING,
      };
      await sendRequest<AssetInterface>(
        CoreAPI_URLs.ASSETS,
        async (responseAsset) => {
          if (responseAsset) {
            const formData = new FormData();
            formData.append("file", providerAssetImage);
            await sendRequest<AssetInterface>(
              CoreAPI_URLs.ASSETS_UPLOAD,
              async (responseAssetUpload) => {
                if (responseAssetUpload) {
                  const providerAssetRelation = {
                    provider: { id: model["model"].id },
                    asset: { id: responseAssetUpload.id },
                  };
                  await sendRequest(
                    CoreAPI_URLs.PROVIDER_ASSET_RELATION,
                    null,
                    "POST",
                    JSON.stringify(providerAssetRelation)
                  );
                }
              },
              "POST",
              formData,
              [{ header: HeaderEnum.RESOURCE_ID, value: responseAsset.id }]
            );
          }
        },
        "POST",
        JSON.stringify(assetData)
      );
    }
    if (ban) {
      if (props.postProcessingHandler) {
        props.postProcessingHandler(model, parentListLocal);
      }
    } else {
      // If redirect is undefined (modal mode), call postProcessingHandler instead of navigating
      if (!props.redirect && props.postProcessingHandler) {
        props.postProcessingHandler(model, parentListLocal || []);
      } else if (props.redirect) {
        setTimeout(() => {
          navigate(props.redirect, { replace: true });
        }, 1000);
      }
    }
  };

  const modelSubmitHandler = (model: Partial<T>) => {
    setNewModel(model as T);
    setActiveStep(activeStep + 1);
  };

  const personSubmitHandler = async (
    person: Partial<PersonInterface>,
    isSubmit: boolean,
    selectedPersons: Array<ExtendedPersonInterface>
  ) => {
    const model = { ...newModel, person: person } as unknown as T;
    setNewModel(model);
    ban = isSubmit;

    const shouldForceSubmit = props.type === UserRolesEnum.PATIENT && (!shouldCreatePatientUser || isEditMode) && isTheLastStep;
    const effectiveIsSubmit = isSubmit || shouldForceSubmit;

    // If person already has a user account and no parent processing needed
    // Skip UserForm and Entity form, go directly to processModel (same as patient flow)
    if (effectiveIsSubmit && person.user?.id && selectedPersons.length === 0) {
      // Check if person already has the entity we're trying to create
      const entityType = getUserRoleEntityType(props.type);

      if (entityType && person[entityType]?.id) {
        // Person already has this entity type - show error
        const entityName = entityType === EntityTypeEnum.PATIENT ? t("stepperForm.patient") : t("stepperForm.provider");
        setErrorModalContent({
          title: t("stepperForm.personAlreadyHasEntity", {
            name: `${person.name} ${person.lastname}`,
            entity: entityName
          }),
          message: t("stepperForm.cannotCreateDuplicateEntity")
        });
        setOpenErrorModal(true);
        return;
      }
      parentListLocal = [];
      // Reset ban to false to enable navigation after model creation
      ban = false;

      const reuseModel = {
        ...newModel,
        person: person,
      } as unknown as T;

      processModel(reuseModel);
      return;
    }

    if (!effectiveIsSubmit) {
      setActiveStep(activeStep + 1);
    } else {
      for (let index = 0; index < selectedPersons.length; index++) {
        if (!selectedPersons[index].existAccount) {
          if (selectedPersons[index].responsible) {
            const userModel = {
              username: selectedPersons[index].username,
              email: selectedPersons[index].email,
              roles: [UserRolesEnum.PATIENT],
            };
            await sendRequest<T>(
              CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
              async (response) => {
                if (response) {
                  const itemUser = {
                    ...selectedPersons[index],
                    user: response,
                  };
                  await sendRequest<ExtendedPersonInterface>(
                    CoreAPI_URLs.PERSONS_GENERAL_OPERATIONS,
                    async (response) => {
                      if (response) {
                        selectedPersons[index] = {
                          ...(selectedPersons[index] = response),
                        };
                        selectedPersons[index].idlocal = response.id;
                      }
                    },
                    "POST",
                    JSON.stringify(itemUser),
                    null,
                    { successMessage: t("stepperForm.creationSuccess") }
                  );
                }
              },
              "POST",
              JSON.stringify(userModel)
            );
          } else {
            await sendRequest<ExtendedPersonInterface>(
              CoreAPI_URLs.PERSONS_GENERAL_OPERATIONS,
              async (response) => {
                if (response) {
                  selectedPersons[index] = {
                    ...(selectedPersons[index] = response),
                  };
                  selectedPersons[index].idlocal = response.id;
                }
              },
              "POST",
              JSON.stringify(selectedPersons[index]),
              null,
              { successMessage: t("stepperForm.creationSuccess") }
            );
          }
        }
      }
      parentListLocal = selectedPersons;
      model["person"]["user"] = undefined;
      processModel(model as unknown as T);
    }
  };

  const userSubmitHandler = (user: Partial<UserInterface>) => {
    const model = { ...newModel, person: { ...newModel["person"], user: user } };
    setNewModel(model);
    processModel(model as unknown as T);
  };

  const getJSXFormSteps = (stepIndex: number) => {
    if ([UserRolesEnum.ADMIN, UserRolesEnum.ROOT, UserRolesEnum.MAINTAINER, UserRolesEnum.SECRETARY_RECEPTIONIST].includes(props.type)) {
      switch (stepIndex) {
        case 0:
          return (
            <PersonForm
              model={getValidPersonModel(newModel["person"])}
              action={FormActionType.NEW}
              nextStep={personSubmitHandler}
              backStepHandler={backStepHandler}
              lastStep={false}
              firstStep={true}
              type={props.type}
              setImage={setImage}
            />
          );
        case 1:
          return (
            <UserCreationForm
              model={getValidUserModel(newModel["person"]?.user)}
              action={FormActionType.NEW}
              nextStep={userSubmitHandler}
              backStepHandler={backStepHandler}
              lastStep={true}
              firstStep={false}
              type={props.type}
            />
          );
        default:
          return <></>;
      }
    }

    if (props.type === UserRolesEnum.PATIENT && (!shouldCreatePatientUser || isEditMode)) {
      switch (stepIndex) {
        case 0:
          return (
            <EntityModelForm
              model={newModel}
              isDoctor={props.isDoctor}
              nextStep={modelSubmitHandler}
              backStep={backStepHandler}
              lastStep={false}
              firstStep={true}
              type={props.type}
              setPharmacyRelationList={setPharmacyRelationList}
              setDefaultPharmacy={setDefaultPharmacy}
              setInsuranceRelationList={setInsuranceRelationList}
              setProviderAssetImage={setProviderAssetImage}
            />
          );
        case 1:
          return (
            <PersonForm
              model={getValidPersonModel(newModel["person"])}
              action={FormActionType.NEW}
              nextStep={personSubmitHandler}
              backStepHandler={backStepHandler}
              lastStep={true}
              firstStep={false}
              type={props.type}
              setImage={setImage}
              comesFromTable={false}
            />
          );
        default:
          return <></>;
      }
    }
    let formType = props.type;
    if (props.type === UserRolesEnum.DOCTOR || props.type === UserRolesEnum.NURSE || props.type === UserRolesEnum.HEAD_NURSE) {
      formType = UserRolesEnum.DOCTOR;
    }
    switch (stepIndex) {
      case 2:
        if (props.type === UserRolesEnum.PATIENT && isEditMode) {
          return <></>;
        }
        return (
          <UserCreationForm
            model={getValidUserModel(newModel["person"]?.user)}
            action={FormActionType.NEW}
            nextStep={userSubmitHandler}
            backStepHandler={backStepHandler}
            lastStep={isTheLastStep}
            firstStep={isTheFirstStep}
            type={props.type}
          />
        );
      case 1:
        return (
          <PersonForm
            model={getValidPersonModel(newModel["person"])}
            action={FormActionType.NEW}
            nextStep={personSubmitHandler}
            backStepHandler={backStepHandler}
            lastStep={isTheLastStep}
            firstStep={isTheFirstStep}
            type={props.type}
            setImage={setImage}
            comesFromTable={false}
          />
        );
      case 0:
        return (
          <EntityModelForm
            model={newModel}
            isDoctor={props.isDoctor}
            nextStep={modelSubmitHandler}
            backStep={backStepHandler}
            lastStep={isTheLastStep}
            firstStep={isTheFirstStep}
            type={formType}
            setPharmacyRelationList={setPharmacyRelationList}
            setDefaultPharmacy={setDefaultPharmacy}
            setInsuranceRelationList={setInsuranceRelationList}
            setProviderAssetImage={setProviderAssetImage}
          />
        );
      default:
        return <></>;
    }
  };

  let title: string = props.type;
  if (title === "PATIENT") {
    title = t("stepperForm.newPatient");
  } else if (title === "DOCTOR") {
    title = t("stepperForm.newDoctor");
  } else if (title === "NURSE") {
    title = t("stepperForm.newNurse");
  } else if (title === "HEAD NURSE") {
    title = t("stepperForm.newHeadNurse");
  } else if (title === "MAINTAINER") {
    title = t("stepperForm.newMaintainer");
  } else if (title === "SECRETARY/RECEPTIONIST") {
    title = t("stepperForm.newSecretaryReceptionist");
  } else if (title === "ADMIN") {
    title = t("stepperForm.newAdmin");
  }

  const getFormSteps = (props: Props<T>) => {
    const formStep: FormStep[] = [];

    let type: string = props.type;
    if (type === "PATIENT") {
      type = t("stepperForm.patientData");
      title = t("stepperForm.newPatient");
    } else if (type === "DOCTOR") {
      type = t("stepperForm.doctorData");
      title = t("stepperForm.newDoctor");
    } else if (type === "NURSE") {
      type = t("stepperForm.nurseData");
      title = t("stepperForm.newNurse");
    } else if (type === "HEAD_NURSE") {
      type = t("stepperForm.headNurseData");
      title = t("stepperForm.newHeadNurse");
    }

    const step1: FormStep = {
      id: "newStepModelStep1",
      label: type,
    };

    const step2: FormStep = {
      id: "newStepPersonStep2",
      label: t("stepperForm.personData"),
    };

    const step3: FormStep = {
      id: "newStepUserStep3",
      label: t("stepperForm.userData"),
    };

    if (![UserRolesEnum.ADMIN, UserRolesEnum.ROOT, UserRolesEnum.MAINTAINER, UserRolesEnum.SECRETARY_RECEPTIONIST].includes(type as UserRolesEnum)) {
      formStep.push(step1);
    }
    formStep.push(step2);

    if (props.type !== UserRolesEnum.PATIENT || (shouldCreatePatientUser && !isEditMode)) {
      formStep.push(step3);
    }

    return formStep;
  };

  const formSteps = useMemo<FormStep[]>(() => getFormSteps(props), [props.type, shouldCreatePatientUser, isEditMode]);

  return (
    <Box py={3} sx={{ width: "100%" }}>
      <Typography variant="h3" width={"90%"} m={"0 auto"}>
        {title}
      </Typography>
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%", mt: 8 }}>
        <Grid item xs={12}>
          <Card style={{ width: "100%" }} className="data-view">
            <Box mx={2} mt={-3}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {formSteps.map((formStep) => (
                  <Step key={`step-header-${formStep.id}`}>
                    <StepLabel>{formStep.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box p={3}>{getJSXFormSteps(activeStep)}</Box>
          </Card>
        </Grid>
      </Grid>
      <Dialog
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">
          {errorModalContent.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {errorModalContent.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorModal(false)} color="primary" name="cy-passwordValidation-ok">
            {t("stepperForm.ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewStepperForm;
